/**
 * Fixed-point coordinates for the swept brush: 1/16 voxel per unit.
 *
 * Cell (x, y, z) has its centre at integer voxel coordinates (x, y, z), the same convention as
 * `rasterizeEllipsoid` in `src/shapes.ts`. In fixed point the centre of cell x is at `x * FX_ONE`.
 *
 * All brush geometry (`insideFx`, `rowSpan`) is evaluated in integer fixed-point arithmetic so that
 * results are bit-exact and independent of evaluation order; floats are used only for estimates
 * that are then corrected with the exact integer predicate.
 */

export const FX_SHIFT = 4
export const FX_ONE = 1 << FX_SHIFT // 16

/** A point in fixed-point document space. */
export type Fx3 = readonly [number, number, number]

/** Voxel units -> fixed point (rounded to the nearest 1/16 voxel). */
export function toFx(v: number): number {
  return Math.round(v * FX_ONE)
}

/** Fixed point -> voxel units. */
export function fromFx(fx: number): number {
  return fx / FX_ONE
}

/** The fixed-point centre of integer cell (x, y, z). */
export function cellToFx(x: number, y: number, z: number): Fx3 {
  return [x * FX_ONE, y * FX_ONE, z * FX_ONE]
}

/** Smallest cell index whose centre is >= the fixed-point coordinate. */
export function ceilCell(fx: number): number {
  return Math.ceil(fx / FX_ONE)
}

/** Largest cell index whose centre is <= the fixed-point coordinate. */
export function floorCell(fx: number): number {
  return Math.floor(fx / FX_ONE)
}

/** Euclidean distance between two fixed-point points, in voxels. */
export function distanceVoxels(a: Fx3, b: Fx3): number {
  const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2]
  return Math.sqrt(dx * dx + dy * dy + dz * dz) / FX_ONE
}

export function fx3Equal(a: Fx3 | null | undefined, b: Fx3 | null | undefined): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2]
}
